'use client'

import type { ExperimentClient, Variants } from '@amplitude/experiment-js-client'
import { usePathname, useRouter } from 'next/navigation'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { configPublic } from '@src/config.public'
import { useEffectOnce } from '@src/hooks/useEffectOnce'
import { getUnlocalizedPath } from '@src/i18n/locale.util'
import { ServerActions } from '@src/services/server-actions.service'
import { serializeExperiments } from '@src/utils/amplitude-helpers'
import { COOKIES } from '@src/utils/constants.util'
import { getBrowserCookie } from '@src/utils/cookies.util'
import { useExperimentContext } from '@src/utils/experimentProvider'
import type { FlowRedirectApplied, FlowRedirectRule } from '@src/utils/flow-router.util'
import { log } from '@src/utils/log.util'
import { safeParse } from '@src/utils/string.util'

const flowRouterContext = createContext({
  flowRedirectRules: [] as FlowRedirectRule[],
})

const useFlowRouterContext = () => {
  return useContext(flowRouterContext)
}

export function FlowRouterProvider({ children }: { children: React.ReactNode }) {
  const [flowRedirectRules, setFlowRedirectRules] = useState<FlowRedirectRule[]>([])

  useEffectOnce(() => {
    // Skip flow router functionality when Shopify feature flag is enabled
    if (configPublic.featureFlags.shopifyEnabled) {
      log.info('[FlowRouter] Skipping flow router - Shopify feature flag is enabled')
      return
    }

    void ServerActions.parallel
      .getFlowRouterRules({ addUserAttributes: false })
      .then(setFlowRedirectRules)
      .catch((error) => {
        log.error('Failed to get FlowRouter rules', error)
      })
  }, [])

  return (
    <flowRouterContext.Provider value={{ flowRedirectRules }}>
      {children}
    </flowRouterContext.Provider>
  )
}

/**
 * This hook is used to set the last url cookie.
 * The cookie is used by the FlowRouter to determine from which website page the user came from.
 */
export function useUrlTracker() {
  const pathname = usePathname()

  useEffect(() => {
    // Skip URL tracking when Shopify feature flag is enabled
    if (configPublic.featureFlags.shopifyEnabled) return
    if (pathname) void updateLastUrl(pathname)
  }, [pathname])

  const updateLastUrl = async (pathname: string) => {
    const url = new URL(location.href)
    url.pathname = pathname
    void ServerActions.parallel.setCookie(COOKIES.lastUrl, location.href)
  }
}

/**
 * This hook is used to determine if the Next.js Router cache should be cleared.
 * For example, when the user is redirected with the FlowRouter.
 * @returns boolean
 */
export function useRouterCacheClear() {
  const pathname = usePathname()
  const router = useRouter()
  const { flowRedirectRules } = useFlowRouterContext()

  // Clear the Next.js Router Cache when the current route is targeted by a FlowRouterRule
  const currentRouteTargeted = useMemo(() => {
    if (!pathname || !router) return false
    const clear = flowRedirectRules.some((r) => r.fromRoute === getUnlocalizedPath(pathname))
    return clear
  }, [pathname, router, flowRedirectRules])

  // Clear the Next.js Router Cache when the user is redirected with the FlowRouter
  const redirectApplied = useMemo(() => {
    if (!pathname || !router) return false
    const appliedRedirectsCookie = getBrowserCookie(COOKIES.flowRedirectApplied)
    const appliedRedirects = safeParse(appliedRedirectsCookie ?? '[]') as FlowRedirectApplied[]
    const clear = appliedRedirects.some((r) => r.redirectRoute === getUnlocalizedPath(pathname))
    if (!clear) return false
    return true
  }, [pathname, router])

  // Skip router cache clearing when Shopify feature flag is enabled
  if (configPublic.featureFlags.shopifyEnabled) return false

  // INFO: other conditions to clear the cache can be added here
  const shouldClearCache = redirectApplied || currentRouteTargeted

  return shouldClearCache
}

/**
 * This function is used to expose the Amplitude experiments used in FlowRouterRules.
 * It exposes the variant for the rule that applied,
 * and exposes the control variant for rules that didn't apply.
 */
export function useFlowRouterExperiments() {
  const pathname = usePathname()
  const { experimentClient, allVariants } = useExperimentContext() ?? {}

  useEffect(() => {
    // Skip flow router experiments when Shopify feature flag is enabled
    if (configPublic.featureFlags.shopifyEnabled) return
    if (pathname && experimentClient && allVariants)
      void exposeExperimentControl(pathname, experimentClient, allVariants)
  }, [pathname, experimentClient, allVariants])

  const exposeExperimentControl = async (
    pathname: string,
    experiment: ExperimentClient,
    variants: Variants
  ) => {
    const variantsSerialized = serializeExperiments(variants).replaceAll(' ', '')
    const currentRoute = getUnlocalizedPath(pathname)
    const allFlowRedirectRules = await ServerActions.parallel.getFlowRouterRules({
      addUserAttributes: false,
    })

    const appliedRedirectsCookie = getBrowserCookie(COOKIES.flowRedirectApplied) ?? '[]'
    const appliedRedirects = safeParse(appliedRedirectsCookie) as FlowRedirectApplied[]
    const appliedRedirect = appliedRedirects.find((r) => r.redirectRoute === currentRoute)
    const appliedRule = allFlowRedirectRules.find((r) => r.id === appliedRedirect?.ruleId)
    const appliedRuleExperiment = getExperimentFromRule(appliedRule)

    // expose the variant for rule that applied
    if (appliedRuleExperiment) {
      exposeToAmplitude(experiment, appliedRuleExperiment)
    }

    // expose the control variant for rules that didn't apply
    const notAppliedRulesWithExperiment = allFlowRedirectRules
      .filter((rule) => !appliedRedirects.some((r) => r.ruleId === String(rule.id)))
      .filter((rule) => rule.toRoute === currentRoute)
      .filter((rule) => {
        const ruleExperimentControl = String(rule.amplitudeExperimentControl?.replaceAll(' ', ''))
        const matchedControl = variantsSerialized.includes(ruleExperimentControl)
        return matchedControl
      })

    notAppliedRulesWithExperiment.forEach((rule) => {
      exposeToAmplitude(experiment, rule.amplitudeExperimentControl)
    })
  }
}

export function FlowRouterExperiments() {
  useFlowRouterExperiments()
  return null
}

function getExperimentFromRule(rule?: FlowRedirectRule) {
  return rule?.query?.find((q) => q.property === 'amplitudeExperiment')?.value ?? ''
}

function exposeToAmplitude(experiment: ExperimentClient, variant: string) {
  const builderKey = variant.split(':')[0]
  const builderValue = variant.split(':')[1]?.trim()
  const amplitudeVariant = experiment.variant(builderKey)
  const amplitudeValue = amplitudeVariant.value

  if (!!builderValue && !!amplitudeValue && builderValue === amplitudeValue) {
    // @ts-expect-error - exposureTrackingProvider is not in the types
    if (experiment.exposureTrackingProvider) experiment.exposureTrackingProvider.tracked = {}
    experiment.exposure(builderKey)
  }
}
